import { useState } from 'react';
import type { ReactElement } from 'react';
import type { ShoppingUnit } from '../../api/shoppingTypes.js';

export interface AddItemFormProps {
  disabled: boolean;
  onSubmit: (req: {
    list_id: number;
    name: string;
    amount: number;
    unit: ShoppingUnit;
  }) => Promise<void>;
  listId: number;
}

const UNITS: ShoppingUnit[] = ['g', 'ml', 'tbsp', 'tsp', 'pcs'];

export function AddItemForm({
  disabled,
  onSubmit,
  listId,
}: AddItemFormProps): ReactElement {
  const [open, setOpen] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [amount, setAmount] = useState<string>('1');
  const [unit, setUnit] = useState<ShoppingUnit>('pcs');
  const [busy, setBusy] = useState<boolean>(false);

  async function handleSubmit(): Promise<void> {
    const amt = Number.parseFloat(amount);
    if (name.trim().length === 0 || !Number.isFinite(amt) || amt < 0) return;
    setBusy(true);
    try {
      await onSubmit({
        list_id: listId,
        name: name.trim(),
        amount: amt,
        unit,
      });
      setName('');
      setAmount('1');
      setUnit('pcs');
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className="flex min-h-[40px] w-full items-center justify-center rounded-md border text-sm"
        style={{
          borderColor: 'var(--accent)',
          color: 'var(--accent)',
          background: 'transparent',
        }}
        data-testid="shopping-add-toggle"
      >
        + Add item manually
      </button>
    );
  }

  return (
    <div
      className="flex flex-col gap-2 rounded-lg border p-3"
      style={{ borderColor: 'var(--border)' }}
      data-testid="shopping-add-form"
    >
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name"
        className="rounded border px-2 py-1 text-sm"
        style={{ borderColor: 'var(--border)', color: 'var(--text-main)' }}
        data-testid="shopping-add-name"
      />
      <div className="flex gap-2">
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          min="0"
          className="w-20 rounded border px-2 py-1 text-sm"
          style={{ borderColor: 'var(--border)', color: 'var(--text-main)' }}
          data-testid="shopping-add-amount"
        />
        <select
          value={unit}
          onChange={(e) => setUnit(e.target.value as ShoppingUnit)}
          className="rounded border px-2 py-1 text-sm"
          style={{ borderColor: 'var(--border)', color: 'var(--text-main)' }}
          data-testid="shopping-add-unit"
        >
          {UNITS.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </select>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={busy}
          className="flex min-h-[40px] flex-1 items-center justify-center rounded-md text-sm"
          style={{
            background: 'var(--accent)',
            color: 'var(--accent-on)',
            opacity: busy ? 0.6 : 1,
          }}
          data-testid="shopping-add-submit"
        >
          {busy ? 'Adding…' : 'Add'}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="flex min-h-[40px] items-center rounded-md border px-3 text-sm"
          style={{
            borderColor: 'var(--border)',
            color: 'var(--text-muted)',
            background: 'transparent',
          }}
          data-testid="shopping-add-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
